import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { getInitialQueryWithTableSettings } from "components/Table/tableUtils";
import Tooltip from "components/Tooltip/Tooltip";
import { objectTemplateAttributeListKey } from "containers/Resources/screens/ResourcesSingle/ResourcesSingle";
import { AttributeReorderModal } from "containers/Resources/screens/ResourcesSingle/components/AttributeReorderModal";
import { SubrecordTable } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import { SubrecordTableProps } from "containers/Resources/screens/ResourcesSingle/components/UserGitPublicKeyList";
import { Object_Template } from "graphql/generated/graphql";
import { useCallback, useMemo, useState } from "react";
import { getRelativeUrl } from "utils/service";

interface ObjectTemplateAttributeSubrecordProps {
    objectTemplateList: Object_Template[];
    updateAttributeListQuery: (query: any) => void;
    initialQuery: any;
}

export const ObjectTemplateAttributeList = ({
    resourceData,
    onResourceClick,
    createSubrecord,
    fetchResourceData,
    objectTemplateList,
    isFieldDisabled,
    updateAttributeListQuery,
    initialQuery,
}: SubrecordTableProps<Object_Template> & ObjectTemplateAttributeSubrecordProps) => {
    const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
    const objectTemplateAttributeData = useMemo(
        () =>
            resourceData?.object_template_attribute_list_object_template_id?.object_template_attribute_list?.map(
                (attribute) => ({
                    ...attribute,
                    deleted_at: resourceData?.deleted_at,
                    is_readonly: resourceData?.is_readonly,
                    resource_type: "object_template_attribute",
                })
            ),
        [resourceData]
    );
    const objectTemplateAttributeCount =
        resourceData?.object_template_attribute_list_object_template_id
            ?.object_template_attribute_count || 0;

    const openObjectTemplateAttributeForm = useCallback(
        (e, id, resource_type) =>
            onResourceClick({
                e,
                id,
                resource_type,
            }),
        [onResourceClick]
    );

    // Read-only "Sort order" column showing each attribute's persisted
    // object_template_attribute.sort_order. The table defaults to sort_order
    // ascending (via attributeInitialQuery); reordering is done through the
    // "Re-order" modal (AttributeReorderModal), which persists the whole order
    // in a single update.
    const sortOrderColumn = {
        label: "Sort order",
        key: "sort_order",
        shown: true,
        sortable: true,
        dto: ({ sort_order, id, resource_type }) => {
            return (
                <RecordLabel
                    key={id}
                    link={getRelativeUrl(resource_type, id)}
                    name={
                        sort_order === null || sort_order === undefined
                            ? ""
                            : String(sort_order)
                    }
                    onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                />
            );
        },
    };

    const columns = [
        {
            label: "Name",
            key: "name",
            shown: true,
            sortable: true,
            dto: ({ name, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={name}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Datatype",
            key: "datatype",
            shown: true,
            sortable: true,
            dto: ({ datatype, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        name={datatype}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Reference",
            key: "reference_name",
            shown: true,
            sortable: false,
            dto: ({ reference_object_template_id, reference_record_type, id, resource_type }) => {
                if (reference_object_template_id) {
                    // For OBJECT_TEMPLATE_REFERENCE attributes, link the referenced
                    // template's name to its object_template screen — the same
                    // treatment as the Used-by "Object template" column and the
                    // incoming-reference element. No onClick: clicking navigates
                    // to the referenced template, not the attribute edit form.
                    const referencedTemplate = objectTemplateList.find(
                        (objectTemplate) => objectTemplate.id === reference_object_template_id
                    );
                    return (
                        <RecordLabel
                            key={id}
                            link={getRelativeUrl("object_template", reference_object_template_id)}
                            iconByType="object_template"
                            name={referencedTemplate?.name ?? reference_object_template_id}
                        />
                    );
                } else if (reference_record_type) {
                    return (
                        <RecordLabel
                            key={id}
                            link={getRelativeUrl(resource_type, id)}
                            name={reference_record_type}
                            onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                        />
                    );
                } else {
                    return null;
                }
            },
        },
        {
            // Shown as "Required" (= !is_nullable), matching the attribute editor.
            label: "Required",
            key: "is_nullable",
            shown: true,
            sortable: true,
            dto: ({ is_nullable, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        icon={is_nullable === false ? IconCollection.check : undefined}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Is unique",
            key: "is_unique",
            shown: true,
            sortable: true,
            dto: ({ is_unique, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        icon={is_unique ? IconCollection.check : undefined}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Is hidden",
            key: "is_hidden",
            shown: true,
            sortable: true,
            dto: ({ is_hidden, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        icon={is_hidden ? IconCollection.check : undefined}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Silent update",
            key: "silent_update",
            shown: true,
            sortable: true,
            dto: ({ silent_update, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        icon={silent_update ? IconCollection.check : undefined}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        {
            label: "Searchable",
            key: "searchable",
            shown: true,
            sortable: true,
            dto: ({ searchable, id, resource_type }) => {
                return (
                    <RecordLabel
                        key={id}
                        link={getRelativeUrl(resource_type, id)}
                        icon={searchable ? IconCollection.check : undefined}
                        onClick={(e) => openObjectTemplateAttributeForm(e, id, resource_type)}
                    />
                );
            },
        },
        sortOrderColumn,
    ];

    const canReorder = !isFieldDisabled && objectTemplateAttributeCount > 1;
    const reorderButton = (
        <Tooltip
            overlay={
                canReorder
                    ? "Drag all attributes into a new order and save it in one step"
                    : "Add at least two attributes to re-order them"
            }
        >
            <span>
                <Button
                    size="small"
                    icon={IconCollection.sort}
                    label="Re-order"
                    variation="dark"
                    onClick={() => setIsReorderModalOpen(true)}
                    disabled={!canReorder}
                />
            </span>
        </Tooltip>
    );

    return (
        <>
            <SubrecordTable
                key="objectTemplateAttributeList"
                data={objectTemplateAttributeData as any[]}
                uniqueKey="objectTemplateAttributeList"
                title="Attributes"
                onAddClick={createSubrecord}
                isActionButtonDisabled={isFieldDisabled}
                refreshData={fetchResourceData}
                columns={columns}
                headerAction={reorderButton}
                headerActionPlacement="after"
                emptyListText="No attributes."
                initialQuery={getInitialQueryWithTableSettings(
                    objectTemplateAttributeListKey,
                    initialQuery
                )}
                recordLabel="attribute"
                updateQuery={updateAttributeListQuery}
                totalItemCount={objectTemplateAttributeCount}
                tableSettingsKey={objectTemplateAttributeListKey}
                tableOptions={{ viewColumns: true }}
            />
            {resourceData?.id && (
                <AttributeReorderModal
                    isOpen={isReorderModalOpen}
                    onClose={() => setIsReorderModalOpen(false)}
                    objectTemplateId={resourceData.id}
                    onSaved={fetchResourceData}
                />
            )}
        </>
    );
};
